/**
 * src/shell/components/MobileDeviceSimulator.tsx
 *
 * In-App Device Emulator & Viewport Tester.
 * Dev/staging-only tool to preview responsive layouts inside the app.
 *
 * Features:
 * - Preset device frames (iPhone 15 Pro, Pixel 8, iPad Mini, Custom)
 * - Portrait ↔ landscape toggle
 * - Touch event emulation toggle with visual ripple
 * - Scale zoom slider (50–100%)
 * - Safe-area inset and notch overlays
 * - Keyboard shortcut: Ctrl+Shift+M to toggle
 *
 * NOT included in production builds (controlled by VITE_DEV_TOOLS env var
 * or always available in non-production NODE_ENV).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

// ─── Device Profiles ──────────────────────────────────────────────────────────

export interface DeviceProfile {
  id: string;
  name: string;
  portraitWidth: number;
  portraitHeight: number;
  /** CSS-approximate notch/island safe inset at the top */
  notchTopPx: number;
  /** Bottom home indicator area */
  homeBarPx: number;
  category: 'phone' | 'tablet';
  icon: string;
}

const DEVICE_PROFILES: DeviceProfile[] = [
  {
    id: 'iphone15pro',
    name: 'iPhone 15 Pro',
    portraitWidth: 393,
    portraitHeight: 852,
    notchTopPx: 59,
    homeBarPx: 34,
    category: 'phone',
    icon: '📱',
  },
  {
    id: 'pixel8',
    name: 'Pixel 8',
    portraitWidth: 412,
    portraitHeight: 915,
    notchTopPx: 24,
    homeBarPx: 28,
    category: 'phone',
    icon: '📱',
  },
  {
    id: 'ipadmini',
    name: 'iPad Mini',
    portraitWidth: 744,
    portraitHeight: 1133,
    notchTopPx: 20,
    homeBarPx: 20,
    category: 'tablet',
    icon: '📋',
  },
  {
    id: 'custom',
    name: 'Custom',
    portraitWidth: 375,
    portraitHeight: 667,
    notchTopPx: 20,
    homeBarPx: 0,
    category: 'phone',
    icon: '⚙️',
  },
];

// ─── Touch Ripple ─────────────────────────────────────────────────────────────

interface Ripple {
  id: number;
  x: number;
  y: number;
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface MobileDeviceSimulatorProps {
  /** URL / iframe src to render inside the emulator frame */
  previewUrl?: string;
  onClose: () => void;
}

export const MobileDeviceSimulator: React.FC<MobileDeviceSimulatorProps> = ({
  previewUrl = window.location.href,
  onClose,
}) => {
  const [selectedDeviceId, setSelectedDeviceId] = useState('iphone15pro');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [scale, setScale] = useState(70);
  const [touchEmulation, setTouchEmulation] = useState(true);
  const [showOverlays, setShowOverlays] = useState(true);
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const [customW, setCustomW] = useState(375);
  const [customH, setCustomH] = useState(667);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const nextRippleId = useRef(0);

  const device = DEVICE_PROFILES.find((d) => d.id === selectedDeviceId)!;

  const frameW = orientation === 'portrait' ? device.portraitWidth : device.portraitHeight;
  const frameH = orientation === 'portrait' ? device.portraitHeight : device.portraitWidth;
  const effectiveW = selectedDeviceId === 'custom' ? customW : frameW;
  const effectiveH = selectedDeviceId === 'custom' ? customH : frameH;
  const scaledW = Math.round(effectiveW * scale / 100);
  const scaledH = Math.round(effectiveH * scale / 100);

  // Keyboard shortcut: Ctrl+Shift+M
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'M') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Touch ripple on simulated click
  const handleSimulatedTap = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!touchEmulation) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const id = nextRippleId.current++;
    setRipples((prev) => [...prev, { id, x: e.clientX - rect.left, y: e.clientY - rect.top }]);
    setTimeout(() => setRipples((prev) => prev.filter((r) => r.id !== id)), 600);
  }, [touchEmulation]);

  const topInset = showOverlays ? device.notchTopPx * (scale / 100) : 0;
  const bottomInset = showOverlays ? device.homeBarPx * (scale / 100) : 0;

  // ─── Styles ───────────────────────────────────────────────────────────────

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    background: 'rgba(2, 6, 23, 0.95)',
    backdropFilter: 'blur(20px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
    color: '#f8fafc',
    overflow: 'hidden',
  };

  const toolbarStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    padding: '12px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(15,23,42,0.8)',
    flexShrink: 0,
  };

  const btn = (active?: boolean): React.CSSProperties => ({
    padding: '5px 12px',
    borderRadius: 8,
    border: `1px solid ${active ? '#3b82f6' : 'rgba(255,255,255,0.12)'}`,
    background: active ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.04)',
    color: active ? '#93c5fd' : '#cbd5e1',
    fontSize: 12,
    fontFamily: 'monospace',
    cursor: 'pointer',
    minHeight: 32,
    transition: 'all 0.15s',
  });

  const deviceFrameStyle: React.CSSProperties = {
    width: scaledW,
    height: scaledH,
    position: 'relative',
    borderRadius: selectedDeviceId === 'ipadmini' ? 24 * (scale / 100) : 42 * (scale / 100),
    border: '3px solid rgba(255,255,255,0.15)',
    overflow: 'hidden',
    boxShadow: '0 40px 100px rgba(0,0,0,0.8), inset 0 0 0 1px rgba(255,255,255,0.05)',
    background: '#000',
    flexShrink: 0,
  };

  const notchOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: topInset,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  };

  const homeBarOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: bottomInset,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  };

  return (
    <div data-testid="mobile-device-simulator" style={panelStyle}>

      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div style={toolbarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace', color: '#60a5fa' }}>
            📱 Device Simulator
          </span>

          {/* Device selector */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {DEVICE_PROFILES.map((d) => (
              <button
                key={d.id}
                type="button"
                data-testid={`device-btn-${d.id}`}
                onClick={() => setSelectedDeviceId(d.id)}
                style={btn(selectedDeviceId === d.id)}
              >
                {d.icon} {d.name}
              </button>
            ))}
          </div>

          {/* Orientation toggle */}
          <button
            type="button"
            data-testid="orientation-toggle"
            onClick={() => setOrientation((o) => o === 'portrait' ? 'landscape' : 'portrait')}
            style={btn(orientation === 'landscape')}
          >
            {orientation === 'portrait' ? '↔ Landscape' : '↕ Portrait'}
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Touch emulation */}
          <button
            type="button"
            data-testid="touch-emulation-toggle"
            onClick={() => setTouchEmulation((v) => !v)}
            style={btn(touchEmulation)}
          >
            👆 Touch {touchEmulation ? 'ON' : 'OFF'}
          </button>

          {/* Overlay toggle */}
          <button
            type="button"
            onClick={() => setShowOverlays((v) => !v)}
            style={btn(showOverlays)}
          >
            🔲 Overlays
          </button>

          {/* Scale slider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#94a3b8' }}>Scale</span>
            <input
              type="range"
              min={30}
              max={100}
              value={scale}
              data-testid="scale-slider"
              onChange={(e) => setScale(Number(e.target.value))}
              style={{ width: 80, accentColor: '#3b82f6' }}
            />
            <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#60a5fa', minWidth: 32 }}>{scale}%</span>
          </div>

          {/* Close */}
          <button type="button" onClick={onClose} style={{ ...btn(), color: '#f87171', borderColor: '#ef4444' }}>
            ✕ Close <kbd style={{ fontSize: 9, opacity: 0.7 }}>Ctrl+⇧+M</kbd>
          </button>
        </div>
      </div>

      {/* ── Custom dimension inputs ────────────────────────────────────────── */}
      {selectedDeviceId === 'custom' && (
        <div style={{ display: 'flex', gap: 8, padding: '8px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)',
          background: 'rgba(15,23,42,0.5)', width: '100%', flexShrink: 0 }}>
          <label style={{ fontSize: 12, fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 6 }}>
            W:
            <input
              type="number"
              value={customW}
              min={280}
              max={1600}
              onChange={(e) => setCustomW(Number(e.target.value))}
              style={{ width: 64, padding: '2px 6px', borderRadius: 6, background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)', color: '#f8fafc', fontSize: 12 }}
            />
          </label>
          <label style={{ fontSize: 12, fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 6 }}>
            H:
            <input
              type="number"
              value={customH}
              min={480}
              max={2400}
              onChange={(e) => setCustomH(Number(e.target.value))}
              style={{ width: 64, padding: '2px 6px', borderRadius: 6, background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)', color: '#f8fafc', fontSize: 12 }}
            />
          </label>
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#64748b', alignSelf: 'center' }}>
            px (portrait)
          </span>
        </div>
      )}

      {/* ── Info bar ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 16, padding: '6px 20px', borderBottom: '1px solid rgba(255,255,255,0.05)',
        background: 'rgba(15,23,42,0.4)', fontSize: 11, fontFamily: 'monospace', color: '#64748b',
        width: '100%', flexShrink: 0 }}>
        <span data-testid="device-info-size">
          <span style={{ color: '#93c5fd' }}>{effectiveW}×{effectiveH}</span> px ({orientation})
        </span>
        <span>Scale: {scale}%</span>
        <span style={{ color: topInset > 0 ? '#f59e0b' : '#64748b' }}>
          ↑ Notch: {device.notchTopPx}px
        </span>
        <span style={{ color: bottomInset > 0 ? '#f59e0b' : '#64748b' }}>
          ↓ Home bar: {device.homeBarPx}px
        </span>
      </div>

      {/* ── Device Frame ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, overflow: 'auto', width: '100%' }}>
        <div
          data-testid="device-frame"
          onClick={handleSimulatedTap}
          style={{ ...deviceFrameStyle, cursor: touchEmulation ? 'crosshair' : 'default' }}
        >
          {/* Notch / Dynamic Island overlay */}
          {showOverlays && topInset > 0 && (
            <div style={notchOverlayStyle}>
              <div style={{
                width: selectedDeviceId === 'iphone15pro' ? 38 * (scale / 100) : 20 * (scale / 100),
                height: selectedDeviceId === 'iphone15pro' ? 16 * (scale / 100) : 12 * (scale / 100),
                borderRadius: 99,
                background: 'rgba(0,0,0,0.9)',
                border: '1px solid rgba(255,255,255,0.1)',
              }} />
              <span style={{ position: 'absolute', left: 12, fontSize: 9, color: '#64748b', fontFamily: 'monospace' }}>
                safe-area-top
              </span>
            </div>
          )}

          {/* Iframe content */}
          <iframe
            ref={iframeRef}
            src={previewUrl}
            style={{
              width: effectiveW,
              height: effectiveH,
              border: 'none',
              transform: `scale(${scale / 100})`,
              transformOrigin: 'top left',
              pointerEvents: touchEmulation ? 'none' : 'auto',
            }}
            title={`${device.name} Simulator`}
          />

          {/* Home bar indicator */}
          {showOverlays && bottomInset > 0 && (
            <div style={homeBarOverlayStyle}>
              <div style={{
                width: 100 * (scale / 100),
                height: 4 * (scale / 100),
                borderRadius: 2,
                background: 'rgba(255,255,255,0.35)',
              }} />
              <span style={{ position: 'absolute', right: 12, fontSize: 9, color: '#64748b', fontFamily: 'monospace' }}>
                safe-area-bottom
              </span>
            </div>
          )}

          {/* Touch ripples */}
          {touchEmulation && ripples.map((r) => (
            <div
              key={r.id}
              style={{
                position: 'absolute',
                left: r.x - 20,
                top: r.y - 20,
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'rgba(96,165,250,0.4)',
                animation: 'ripple 0.6s ease-out forwards',
                pointerEvents: 'none',
                zIndex: 20,
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes ripple {
          0%   { transform: scale(0); opacity: 1; }
          100% { transform: scale(2.5); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default MobileDeviceSimulator;
